import { Fragment, memo, useCallback, useMemo, useState } from 'react';

import { CheckIcon, ChevronRightIcon, ExternalLinkIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router';

import { MenuSearchRow } from '@/components/atoms/MenuSearchRow/MenuSearchRow';
import { Command } from '@/components/ui/command';
import {
  PopoverComboboxEmpty,
  PopoverComboboxGroup,
  PopoverComboboxInput,
  PopoverComboboxItem,
  PopoverComboboxList,
  PopoverComboboxTrigger,
} from '@/components/ui/custom/PopoverCombobox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Spinner } from '@/components/ui/spinner';
import { useAllRules } from '@/hooks/useAllRules';
import useBrains from '@/hooks/useBrains';
import useVersions from '@/hooks/useVersions';
import { cn } from '@/lib/utils';
import { selectAccountSlug } from '@/redux/session/selectors';
import { Brain } from '@/shared/types/brain';
import { Version } from '@/shared/types/version';

import { InfoTooltip } from './InfoTooltip/InfoTooltip';
import { byDraftFirst, getVersionLabel, groupRulesByVersion } from './VersionTooltipContent/utils';
import {
  ConnectedRule,
  VersionTooltipContent,
} from './VersionTooltipContent/VersionTooltipContent';
import { Skeleton } from '../ui/skeleton';

export interface BrainVersionSelection {
  brain_id?: string;
  brain_parent_id: string;
  brain_version: number;
  brain_name: string;
}

// cmdk row identifiers — kept in helpers so the encoding stays in one place
const brainValue = (brainId: string) => `brain-${brainId}`;
const versionValue = (brainId: string, version: number) => `version-${brainId}-${version}`;
const parseBrainValue = (value: string) => value.match(/^brain-(.+)$/)?.[1] ?? null;
const parseVersionValue = (value: string) => {
  const match = value.match(/^version-(.+)-\d+$/);
  return match ? { brainId: match[1] } : null;
};

interface VersionRowProps {
  brainId: string;
  brainName: string;
  version: Version;
  isSelected: boolean;
  rulesForVersion: ConnectedRule[] | undefined;
  slug: string | undefined;
  onSelect: () => void;
}

const VersionRow = memo(
  ({
    brainId,
    brainName,
    version,
    isSelected,
    rulesForVersion,
    slug,
    onSelect,
  }: VersionRowProps) => {
    const { t } = useTranslation();
    const showStatus =
      version.status && (version.status === 'training' || version.status === 'unavailable');

    return (
      <PopoverComboboxItem
        value={versionValue(brainId, version.version)}
        onSelect={onSelect}
        className="group h-8 pl-6"
      >
        <InfoTooltip
          content={<VersionTooltipContent version={version} connectedRules={rulesForVersion} />}
          variant="rich"
          side="right"
          align="start"
          size="auto"
        >
          <div className="flex w-full items-center justify-between gap-2">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <span className="truncate">{getVersionLabel(t, version.version)}</span>
            </div>
            {showStatus && (
              <span
                className={cn(
                  'shrink-0 rounded px-1.5 py-0.5 text-xs',
                  version.status === 'training' && 'bg-yellow-100 text-yellow-700',
                  version.status === 'unavailable' && 'bg-gray-100 text-gray-700'
                )}
              >
                {version.status}
              </span>
            )}
            <div className="flex shrink-0 items-center">
              {isSelected && (
                <CheckIcon className="h-4 w-4 text-foreground-muted group-hover:hidden" />
              )}
              <InfoTooltip
                content={`${t('common.go_to')} ${brainName}`}
                side="right"
                size="default"
              >
                <Link
                  to={`/${slug}/ai-agents/${brainId}`}
                  target="_blank"
                  onClick={(e) => e.stopPropagation()}
                  className={cn(
                    'transition-opacity',
                    isSelected ? 'hidden group-hover:block' : 'opacity-0 group-hover:opacity-100'
                  )}
                >
                  <ExternalLinkIcon className="h-3.5 w-3.5 text-foreground-muted hover:text-foreground" />
                </Link>
              </InfoTooltip>
            </div>
          </div>
        </InfoTooltip>
      </PopoverComboboxItem>
    );
  }
);
VersionRow.displayName = 'VersionRow';

interface BrainVersionListProps {
  onSelect: (selection: BrainVersionSelection | null) => void;
  selectedBrainId?: string;
  selectedVersion?: number;
  /** Translation key for the search input. */
  searchPlaceholder?: string;
  /** Shows a back button in place of the search icon, for a list inside another menu. */
  onBack?: () => void;
  className?: string;
}

/** The searchable AI agent and version list, without a popover, so another menu can host it. */
export const BrainVersionList = ({
  onSelect,
  selectedBrainId,
  selectedVersion,
  searchPlaceholder = 'campaigns.setup.search_ai_agents',
  onBack,
  className,
}: BrainVersionListProps) => {
  const { t } = useTranslation();
  const { brains, listStatus } = useBrains();
  const slug = useSelector(selectAccountSlug);
  const [searchQuery, setSearchQuery] = useState('');
  // The list mounts on open, so expand the selected brain if it has versions
  const [expandedBrainId, setExpandedBrainId] = useState<string | null>(() => {
    const selected = brains?.find((b) => b.brain_id === selectedBrainId);
    return selected?.version_count ? selected.brain_id : null;
  });
  const [hoveredDraftBrainId, setHoveredDraftBrainId] = useState<string | null>(null);
  const [activeValue, setActiveValue] = useState('');

  const activeBrainId = expandedBrainId || hoveredDraftBrainId;

  // Fetch versions for the expanded/hovered brain
  const { versions, getStatus: versionsStatus } = useVersions({
    brainId: activeBrainId ?? '',
    enableDiff: false,
  });

  // Fetch rules connected to the active brain (for all versions)
  const { rulesConnected: allRulesForBrain } = useAllRules({ brainId: activeBrainId || undefined });

  // Filter brains by search query, then sort selected first
  const sortedBrains = useMemo(() => {
    if (!brains) {
      return [];
    }
    const query = searchQuery.trim().toLowerCase();
    const filtered = query
      ? brains.filter(
          (brain) =>
            brain.name.toLowerCase().includes(query) || brain.brain_id.toLowerCase().includes(query)
        )
      : brains;
    return [...filtered].sort((a, b) => {
      const aSelected = a.brain_id === selectedBrainId ? -1 : 0;
      const bSelected = b.brain_id === selectedBrainId ? -1 : 0;
      return aSelected - bSelected;
    });
  }, [brains, searchQuery, selectedBrainId]);

  // Sort versions: draft first, then descending
  const sortedVersions = useMemo(() => {
    if (!versions) {
      return [];
    }
    return [...versions].sort(byDraftFirst);
  }, [versions]);

  // Bucket all-rules-for-brain by version once, so per-version row lookup is O(1).
  const rulesByVersion = useMemo(
    () =>
      expandedBrainId
        ? groupRulesByVersion(allRulesForBrain, expandedBrainId)
        : new Map<number, ConnectedRule[]>(),
    [allRulesForBrain, expandedBrainId]
  );

  // Same lift for the hovered draft brain — only computes for the one row that's hovered.
  const rulesForHoveredDraft = useMemo<ConnectedRule[] | undefined>(() => {
    if (!hoveredDraftBrainId || !allRulesForBrain) {
      return undefined;
    }
    const out: ConnectedRule[] = [];
    allRulesForBrain.forEach((rule) => {
      const matches = rule.actions?.some(
        (action) => action.brain_parent_id === hoveredDraftBrainId && action.brain_version === 0
      );
      if (matches) {
        out.push({ rule_id: rule.rule_id, name: rule.name, desk_id: rule.desk_id });
      }
    });
    return out;
  }, [allRulesForBrain, hoveredDraftBrainId]);

  const handleBrainSelect = useCallback(
    (brain: Brain) => {
      if (!brain.version_count || brain.version_count === 0) {
        // Toggle: if already selected, deselect
        if (selectedBrainId === brain.brain_id && selectedVersion === 0) {
          onSelect(null);
          return;
        }
        onSelect({
          brain_id: brain.brain_id,
          brain_parent_id: brain.brain_id,
          brain_version: 0,
          brain_name: brain.name,
        });
      }
    },
    [onSelect, selectedBrainId, selectedVersion]
  );

  // Right arrow expands a versioned brain row; left arrow collapses (or steps out of a version)
  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowRight') {
        const brainId = parseBrainValue(activeValue);
        if (!brainId) {
          return;
        }
        const brain = brains?.find((b) => b.brain_id === brainId);
        if (brain?.version_count && brain.version_count > 0 && expandedBrainId !== brainId) {
          e.preventDefault();
          setExpandedBrainId(brainId);
        }
        return;
      }

      if (e.key === 'ArrowLeft') {
        const parsedVersion = parseVersionValue(activeValue);
        if (parsedVersion) {
          e.preventDefault();
          setExpandedBrainId(null);
          setActiveValue(brainValue(parsedVersion.brainId));
          return;
        }
        const brainId = parseBrainValue(activeValue);
        if (brainId && expandedBrainId === brainId) {
          e.preventDefault();
          setExpandedBrainId(null);
        }
      }
    },
    [activeValue, brains, expandedBrainId]
  );

  const handleVersionSelect = useCallback(
    (brain: Brain, version: Version) => {
      // Toggle: if already selected, deselect
      if (selectedBrainId === brain.brain_id && selectedVersion === version.version) {
        onSelect(null);
        return;
      }
      onSelect({
        brain_parent_id: brain.brain_id,
        brain_version: version.version,
        brain_name: brain.name,
      });
    },
    [onSelect, selectedBrainId, selectedVersion]
  );

  const isLoading = listStatus === 'pending';

  return (
    <Command
      shouldFilter={false}
      value={activeValue}
      onValueChange={setActiveValue}
      className={className}
    >
      {onBack ? (
        <MenuSearchRow
          placeholder={t(searchPlaceholder)}
          onBack={onBack}
          value={searchQuery}
          onValueChange={setSearchQuery}
          onKeyDown={handleSearchKeyDown}
        />
      ) : (
        <PopoverComboboxInput
          placeholder={searchPlaceholder}
          value={searchQuery}
          onValueChange={setSearchQuery}
          onKeyDown={handleSearchKeyDown}
        />
      )}
      <PopoverComboboxList>
        {isLoading ? (
          <div className="p-4 text-center text-sm text-foreground-muted">
            {t('common.loading')}…
          </div>
        ) : sortedBrains.length > 0 ? (
          <PopoverComboboxGroup>
            {sortedBrains.map((brain) => {
              const isSelected = selectedBrainId === brain.brain_id;
              const hasMultipleVersions = brain.version_count && brain.version_count > 0;
              const isExpanded = expandedBrainId === brain.brain_id;
              const isLoadingVersions = isExpanded && versionsStatus === 'pending';

              // Brain with multiple versions: expand/collapse to show versions
              if (hasMultipleVersions) {
                return (
                  <Fragment key={brain.brain_id}>
                    <PopoverComboboxItem
                      value={brainValue(brain.brain_id)}
                      onSelect={() => setExpandedBrainId(isExpanded ? null : brain.brain_id)}
                      className={cn(
                        'h-8 justify-between gap-2',
                        isSelected && 'bg-highlight-background'
                      )}
                    >
                      <span className="min-w-0 flex-1 truncate text-left text-sm leading-5">
                        {brain.name}
                      </span>
                      <div className="flex shrink-0 items-center gap-1">
                        {isLoadingVersions && <Spinner />}
                        <ChevronRightIcon
                          className={cn(
                            'h-3.5 w-3.5 text-foreground-muted transition-transform',
                            isExpanded && 'rotate-90'
                          )}
                        />
                      </div>
                    </PopoverComboboxItem>

                    {isExpanded && isLoadingVersions && (
                      <div className="space-y-1 py-1 pl-6">
                        <div className="flex h-8 items-center justify-between px-2 py-1.5">
                          <Skeleton className="h-4 w-16" />
                          <Skeleton className="h-5 w-20" />
                        </div>
                        <div className="flex h-8 items-center justify-between px-2 py-1.5">
                          <Skeleton className="h-4 w-16" />
                          <Skeleton className="h-5 w-20" />
                        </div>
                      </div>
                    )}

                    {isExpanded &&
                      !isLoadingVersions &&
                      sortedVersions.map((version) => (
                        <VersionRow
                          key={version.version}
                          brainId={brain.brain_id}
                          brainName={brain.name}
                          version={version}
                          isSelected={
                            selectedBrainId === brain.brain_id &&
                            selectedVersion === version.version
                          }
                          rulesForVersion={rulesByVersion.get(version.version)}
                          slug={slug}
                          onSelect={() => handleVersionSelect(brain, version)}
                        />
                      ))}
                  </Fragment>
                );
              }

              // Brain with only draft
              const rulesForDraft =
                brain.brain_id === hoveredDraftBrainId ? rulesForHoveredDraft : undefined;

              const draftVersion = { version: 0, brain_id: brain.brain_id } as unknown as Version;

              return (
                <PopoverComboboxItem
                  key={brain.brain_id}
                  value={brainValue(brain.brain_id)}
                  onSelect={() => handleBrainSelect(brain)}
                  className="group h-8 justify-between gap-2"
                  onMouseEnter={() => setHoveredDraftBrainId(brain.brain_id)}
                  onMouseLeave={() => setHoveredDraftBrainId(null)}
                >
                  <InfoTooltip
                    content={
                      <VersionTooltipContent
                        version={draftVersion}
                        connectedRules={rulesForDraft}
                      />
                    }
                    variant="rich"
                    side="right"
                    align="start"
                    size="auto"
                    showArrow={false}
                  >
                    <div className="flex w-full items-center justify-between">
                      <span className="flex-1 truncate text-sm leading-5">{brain.name}</span>

                      <div className="flex shrink-0 items-center gap-1">
                        {isSelected ? (
                          <>
                            <CheckIcon className="h-4 w-4 text-foreground-muted group-hover:hidden" />
                            <InfoTooltip
                              content={`${t('common.go_to')} ${brain.name}`}
                              side="right"
                              size="default"
                            >
                              <Link
                                to={`/${slug}/ai-agents/${brain.brain_id}`}
                                target="_blank"
                                onClick={(e) => e.stopPropagation()}
                                className="hidden group-hover:block"
                              >
                                <ExternalLinkIcon className="h-3.5 w-3.5 text-foreground-muted hover:text-foreground" />
                              </Link>
                            </InfoTooltip>
                          </>
                        ) : (
                          <>
                            <span className="text-xs text-foreground-muted group-hover:hidden">
                              {t('common.draft')}
                            </span>
                            <InfoTooltip
                              content={`${t('common.go_to')} ${brain.name}`}
                              side="right"
                              size="default"
                            >
                              <Link
                                to={`/${slug}/ai-agents/${brain.brain_id}`}
                                target="_blank"
                                onClick={(e) => e.stopPropagation()}
                                className="hidden group-hover:block"
                              >
                                <ExternalLinkIcon className="h-3.5 w-3.5 text-foreground-muted hover:text-foreground" />
                              </Link>
                            </InfoTooltip>
                          </>
                        )}
                      </div>
                    </div>
                  </InfoTooltip>
                </PopoverComboboxItem>
              );
            })}
          </PopoverComboboxGroup>
        ) : (
          <PopoverComboboxEmpty>{t('campaigns.setup.no_ai_agents_found')}</PopoverComboboxEmpty>
        )}
      </PopoverComboboxList>
    </Command>
  );
};

interface BrainVersionSelectorProps {
  onSelect: (selection: BrainVersionSelection | null) => void;
  initialBrainId?: string;
  initialVersion?: number;
  triggerClassName?: string;
  contentClassName?: string;
  disabled?: boolean;
  placeholder?: string;
  /** Replaces the default combobox button, for example a chip segment. */
  trigger?: React.ReactElement;
  /** Translation key for the search input. */
  searchPlaceholder?: string;
}

export const BrainVersionSelector = ({
  onSelect,
  initialBrainId,
  initialVersion,
  triggerClassName,
  contentClassName,
  disabled = false,
  placeholder,
  trigger,
  searchPlaceholder,
}: BrainVersionSelectorProps) => {
  const { t } = useTranslation();
  const { brains } = useBrains();
  const [selectedBrainId, setSelectedBrainId] = useState<string | undefined>(initialBrainId);
  const [selectedVersion, setSelectedVersion] = useState<number | undefined>(initialVersion);
  const [open, setOpen] = useState(false);

  const handleSelect = (selection: BrainVersionSelection | null) => {
    setSelectedBrainId(selection ? selection.brain_parent_id : '');
    setSelectedVersion(selection?.brain_version);
    onSelect(selection);
    setOpen(false);
  };

  const displayBrainId = selectedBrainId ?? initialBrainId;
  const displayVersion = selectedVersion ?? initialVersion;
  const selectedBrain = brains?.find((b) => b.brain_id === displayBrainId);
  const displayText = selectedBrain
    ? `${selectedBrain.name} - ${getVersionLabel(t, displayVersion)}`
    : (placeholder ?? t('campaigns.setup.ai_agent_placeholder'));

  return (
    <Popover modal open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {trigger ?? (
          <PopoverComboboxTrigger open={open} disabled={disabled} className={triggerClassName}>
            {displayText}
          </PopoverComboboxTrigger>
        )}
      </PopoverTrigger>
      <PopoverContent
        className={cn('w-80 p-0', contentClassName)}
        align="start"
        onCloseAutoFocus={(e) => e.preventDefault()}
        collisionPadding={{ top: 60, bottom: 16 }}
      >
        <BrainVersionList
          onSelect={handleSelect}
          selectedBrainId={selectedBrainId}
          selectedVersion={selectedVersion}
          searchPlaceholder={searchPlaceholder}
        />
      </PopoverContent>
    </Popover>
  );
};
